import { Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { isAuthenticated, setAuth, clearAuth, getDashboardSummary, validateLogin } from './api/client';
import Dashboard from './pages/Dashboard';
import Pins from './pages/Pins';
import AIApproved from './pages/AIApproved';
import Submissions from './pages/Submissions';
import Sites from './pages/Sites';
import Stores from './pages/Stores';
import Users from './pages/Users';
import Stats from './pages/Stats';
import Feedback from './pages/Feedback';
import Availability from './pages/Availability';
import Settings from './pages/Settings';

function Login({ onLogin }: { onLogin: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const valid = await validateLogin(username, password);
      if (valid) {
        setAuth(username, password);
        onLogin();
      } else {
        setError('Invalid credentials');
      }
    } catch {
      setError('Login failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 w-full max-w-md">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">PinCove Admin</h1>
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            className="w-full py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors font-medium"
          >
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
}

function NavItem({ to, icon, label, badge, onClick }: { to: string; icon: string; label: string; badge?: number; onClick?: () => void }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-5 py-3 text-gray-200 no-underline transition-colors border-l-3 text-sm ${
          isActive
            ? 'bg-slate-700 border-l-amber-500 text-white'
            : 'border-l-transparent hover:bg-slate-700 hover:border-l-blue-400'
        }`
      }
    >
      <span className="w-5 text-center">{icon}</span>
      <span>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="ml-auto bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

interface NavBadges {
  pendingPins: number;
  pendingSubmissions: number;
  pendingFeedback: number;
}

function Layout({ onLogout }: { onLogout: () => void }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [badges, setBadges] = useState<NavBadges>({ pendingPins: 0, pendingSubmissions: 0, pendingFeedback: 0 });
  const location = useLocation();

  // Fetch badge counts
  const fetchBadges = useCallback(async () => {
    try {
      const result = await getDashboardSummary();
      if (result.data) {
        setBadges({
          pendingPins: result.data.pins?.pending || 0,
          pendingSubmissions: result.data.submissions?.pending || 0,
          pendingFeedback: result.data.feedback?.pending || 0,
        });
      }
    } catch {
      // Silently fail - badges are not critical
    }
  }, []);

  // Fetch badges on mount and when route changes
  useEffect(() => {
    fetchBadges();
  }, [fetchBadges, location.pathname]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="flex min-h-screen">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-slate-800 text-white px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-slate-700 rounded"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h1 className="text-lg font-bold">PinCove Admin</h1>
        <div className="w-10" />
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        w-60 bg-slate-800 text-white fixed h-screen overflow-y-auto z-50
        transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Header */}
        <div className="px-5 py-5 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold mb-2">PinCove Admin</h1>
            <div className="flex flex-col gap-1">
              <a
                href="https://dash.cloudflare.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 text-xs flex items-center gap-1.5 hover:text-gray-200 no-underline"
              >
                <span>&#x2601;</span> Cloudflare Dashboard <span className="text-[0.65rem]">&#x2197;</span>
              </a>
              <a
                href="https://dash.cloudflare.com/?to=/:account/workers/services/view/pincove-api"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 text-xs flex items-center gap-1.5 hover:text-gray-200 no-underline"
              >
                <span>&#x2699;</span> Workers <span className="text-[0.65rem]">&#x2197;</span>
              </a>
              <a
                href="https://dash.cloudflare.com/?to=/:account/r2/default/buckets/pincove-images"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 text-xs flex items-center gap-1.5 hover:text-gray-200 no-underline"
              >
                <span>&#x1F4BE;</span> R2 Storage <span className="text-[0.65rem]">&#x2197;</span>
              </a>
            </div>
          </div>
          {/* Close button for mobile */}
          <button
            onClick={closeSidebar}
            className="lg:hidden p-2 hover:bg-slate-700 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Data Section */}
        <div className="mt-5 px-5 pb-2 text-xs uppercase text-gray-500 tracking-wider">Data</div>
        <nav>
          <NavItem to="/" icon="&#x1F4CA;" label="Dashboard" onClick={closeSidebar} />
          <NavItem to="/pins" icon="&#x1F4CC;" label="Pins" badge={badges.pendingPins} onClick={closeSidebar} />
          <NavItem to="/ai-approved" icon="&#x1F916;" label="AI-Approved" onClick={closeSidebar} />
          <NavItem to="/submissions" icon="&#x1F4E5;" label="Submissions" badge={badges.pendingSubmissions} onClick={closeSidebar} />
        </nav>

        {/* Locations Section */}
        <div className="mt-5 px-5 pb-2 text-xs uppercase text-gray-500 tracking-wider">Locations</div>
        <nav>
          <NavItem to="/sites" icon="&#x1F3F0;" label="Sites" onClick={closeSidebar} />
          <NavItem to="/stores" icon="&#x1F3EA;" label="Stores" onClick={closeSidebar} />
        </nav>

        {/* Users Section */}
        <div className="mt-5 px-5 pb-2 text-xs uppercase text-gray-500 tracking-wider">Users</div>
        <nav>
          <NavItem to="/users" icon="&#x1F465;" label="Users" onClick={closeSidebar} />
          <NavItem to="/stats" icon="&#x1F3AE;" label="User Stats" onClick={closeSidebar} />
          <NavItem to="/feedback" icon="&#x1F4DD;" label="Feedback" badge={badges.pendingFeedback} onClick={closeSidebar} />
        </nav>

        {/* System Section */}
        <div className="mt-5 px-5 pb-2 text-xs uppercase text-gray-500 tracking-wider">System</div>
        <nav>
          <NavItem to="/availability" icon="&#x1F4CA;" label="Availability" onClick={closeSidebar} />
          <NavItem to="/settings" icon="&#x2699;&#xFE0F;" label="Settings" onClick={closeSidebar} />
        </nav>

        {/* Logout */}
        <div className="mt-8 px-5 pb-5">
          <button
            onClick={onLogout}
            className="w-full py-2 px-4 bg-slate-700 text-gray-300 rounded text-sm hover:bg-slate-600 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 lg:ml-60 pt-14 lg:pt-0 p-4 sm:p-6 lg:p-8 bg-gray-100 min-h-screen">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/pins" element={<Pins />} />
          <Route path="/ai-approved" element={<AIApproved />} />
          <Route path="/submissions" element={<Submissions />} />
          <Route path="/sites" element={<Sites />} />
          <Route path="/stores" element={<Stores />} />
          <Route path="/users" element={<Users />} />
          <Route path="/stats" element={<Stats />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/availability" element={<Availability />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default function App() {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());

  const handleLogin = () => {
    setAuthenticated(true);
  };

  const handleLogout = () => {
    clearAuth();
    setAuthenticated(false);
  };

  if (!authenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return <Layout onLogout={handleLogout} />;
}
